import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { getAdminUsers, createAdminUser, deleteAdminUser, deleteAdminUsers } from '../../services/adminService';
import './UserManager.css';

const UserManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedEmails, setSelectedEmails] = useState(new Set());
  const [processingEmails, setProcessingEmails] = useState(new Set());
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '' });
  const [creating, setCreating] = useState(false);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getAdminUsers();
      setUsers(data || []);
    } catch (err) {
      console.error('Error loading admin users:', err);
      setError(err.message || 'Failed to load admin users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const toggleSelected = (email) => {
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      if (next.has(email)) {
        next.delete(email);
      } else {
        next.add(email);
      }
      return next;
    });
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    setCreating(true);

    try {
      await createAdminUser(newUser);
      setSuccess(`Admin user ${newUser.email} created successfully!`);
      setNewUser({ email: '', password: '', name: '' });
      setShowCreateForm(false);
      await loadUsers();
    } catch (err) {
      console.error('Error creating admin user:', err);
      setError(err.message || 'Failed to create admin user');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async (email) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Are you sure you want to remove admin access for ${email}? This action cannot be undone.`)) {
      return;
    }

    setProcessingEmails((prev) => new Set([...prev, email]));
    setError('');
    setSuccess('');

    try {
      await deleteAdminUser(email);
      setSuccess(`Removed admin access for ${email}.`);
      setSelectedEmails((prev) => {
        const next = new Set(prev);
        next.delete(email);
        return next;
      });
      await loadUsers();
    } catch (err) {
      console.error('Error deleting admin user:', err);
      setError(err.message || 'Failed to remove admin user');
    } finally {
      setProcessingEmails((prev) => {
        const next = new Set(prev);
        next.delete(email);
        return next;
      });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedEmails.size === 0) return;

    const emails = Array.from(selectedEmails);
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Are you sure you want to remove admin access for ${emails.length} user(s)? This action cannot be undone.`)) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      const result = await deleteAdminUsers(emails);
      const successCount = result?.successful?.length ?? emails.length;
      setSuccess(`Removed admin access for ${successCount} user(s).`);
      setSelectedEmails(new Set());
      await loadUsers();
    } catch (err) {
      console.error('Error bulk deleting admin users:', err);
      setError(err.message || 'Failed to remove selected users');
    }
  };

  return (
    <div className="user-manager">
      <div className="manager-header">
        <h2>User Manager</h2>
        <p>Manage admin accounts for the Communication Matters conference app.</p>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="manager-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setShowCreateForm((prev) => !prev)}
        >
          {showCreateForm ? 'Cancel' : '+ Add Admin User'}
        </button>

        {selectedEmails.size > 0 && (
          <button type="button" className="btn btn-danger" onClick={handleBulkDelete}>
            Remove {selectedEmails.size} Selected
          </button>
        )}
      </div>

      {showCreateForm && (
        <form className="create-user-form" onSubmit={handleCreateUser}>
          <div className="form-row">
            <label htmlFor="new-user-email">Email</label>
            <input
              id="new-user-email"
              type="email"
              required
              value={newUser.email}
              onChange={(e) => setNewUser((prev) => ({ ...prev, email: e.target.value }))}
            />
          </div>
          <div className="form-row">
            <label htmlFor="new-user-name">Name</label>
            <input
              id="new-user-name"
              type="text"
              value={newUser.name}
              onChange={(e) => setNewUser((prev) => ({ ...prev, name: e.target.value }))}
            />
          </div>
          <div className="form-row">
            <label htmlFor="new-user-password">Password</label>
            <input
              id="new-user-password"
              type="password"
              required
              minLength={8}
              value={newUser.password}
              onChange={(e) => setNewUser((prev) => ({ ...prev, password: e.target.value }))}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={creating}>
            {creating ? 'Creating…' : 'Create Admin User'}
          </button>
        </form>
      )}

      {loading ? (
        <div className="loading-message">Loading admin users…</div>
      ) : users.length === 0 ? (
        <div className="empty-message">No admin users found.</div>
      ) : (
        <table className="users-table">
          <thead>
            <tr>
              <th scope="col"> </th>
              <th scope="col">Email</th>
              <th scope="col">Name</th>
              <th scope="col">Role</th>
              <th scope="col">Status</th>
              <th scope="col">Created</th>
              <th scope="col"> </th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.email || user.id}>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Select ${user.email}`}
                    checked={selectedEmails.has(user.email)}
                    onChange={() => toggleSelected(user.email)}
                  />
                </td>
                <td>{user.email}</td>
                <td>{user.name || '—'}</td>
                <td>{user.role || 'admin'}</td>
                <td>
                  <span className={`status-badge ${user.is_active === false ? 'status-inactive' : 'status-active'}`}>
                    {user.is_active === false ? 'Inactive' : 'Active'}
                  </span>
                </td>
                <td>{user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-danger btn-small"
                    disabled={processingEmails.has(user.email)}
                    onClick={() => handleDeleteUser(user.email)}
                  >
                    {processingEmails.has(user.email) ? 'Removing…' : 'Remove'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

UserManager.propTypes = {
  user: PropTypes.object,
};

export default UserManager;
